import { getCustomerId } from '../../../utils/cookies';
import { getLoggedInUserMatchKeys } from '../../../utils/userDisplay';
import { getAllRequestsByCustomerID } from './api';
import {
  isWbsSetupRequestInProgress,
  requestBelongsToUser,
} from './wbsSetupImportGuard';
import type { RequestTrackingItem, RequestTrackingStatus } from './types';

/** Canonical requestType for project baseline jobs (flexible matching below). */
export const PROJECT_BASELINE_REQUEST_TYPE = 'PROJECT_BASELINE_CREATE';

export function isProjectBaselineRequestInProgress(status: RequestTrackingStatus): boolean {
  return isWbsSetupRequestInProgress(status);
}

/**
 * Match tracker requestType / source for project baseline creation.
 * Canonical: PROJECT_BASELINE_CREATE; also accepts PROJECT_BASELINE / BASELINE variants.
 */
export function requestTypeIsProjectBaseline(requestType: string | null | undefined): boolean {
  const t = String(requestType ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!t) return false;
  if (t.includes('WBS')) return false;
  return (
    t === PROJECT_BASELINE_REQUEST_TYPE ||
    t === 'PROJECT_BASELINE' ||
    t === 'BASELINE' ||
    t.includes('PROJECT_BASELINE') ||
    (t.includes('BASELINE') && t.includes('CREATE'))
  );
}

function itemLooksLikeProjectBaseline(item: RequestTrackingItem): boolean {
  const type = item.requestType ?? item.source;
  if (requestTypeIsProjectBaseline(type)) {
    return true;
  }
  // Fallback only when type is missing — do not match completed cards titled "Baseline1"
  // via this path if a non-baseline type is present.
  if (type != null && String(type).trim() !== '') {
    return false;
  }
  const haystack = `${item.fileName ?? ''} ${item.title ?? ''} ${item.message ?? ''}`.toLowerCase();
  return haystack.includes('baseline');
}

function itemBelongsToProject(
  item: RequestTrackingItem,
  projectId: string | null | undefined,
): boolean {
  if (!projectId) return true;
  const meta = item.metadata;
  if (meta == null || typeof meta !== 'object') {
    return true;
  }
  const pid =
    (meta as Record<string, unknown>).projectId ??
    (meta as Record<string, unknown>).project_id ??
    (meta as Record<string, unknown>).projectID;
  if (pid == null || String(pid).trim() === '') {
    return true;
  }
  return String(pid) === String(projectId);
}

/** True when a project-baseline request is still active (queued/running only). */
export function isProjectBaselineRequestActive(item: RequestTrackingItem): boolean {
  if (!itemLooksLikeProjectBaseline(item)) {
    return false;
  }
  // Only block on explicitly in-flight statuses. Completed baselines are named
  // "Baseline0"/"Baseline1" and must not be treated as active.
  return isProjectBaselineRequestInProgress(item.status);
}

export async function fetchActiveProjectBaselineForCurrentUser(options?: {
  projectId?: string | null;
}): Promise<{
  blocked: boolean;
  activeRequest: RequestTrackingItem | null;
}> {
  const matchKeys = getLoggedInUserMatchKeys();
  const projectId = options?.projectId ?? null;
  const items = await getAllRequestsByCustomerID(getCustomerId());
  const activeRequest =
    items.find(
      (item) =>
        isProjectBaselineRequestActive(item) &&
        requestBelongsToUser(item.userName, matchKeys) &&
        itemBelongsToProject(item, projectId),
    ) ?? null;

  return { blocked: activeRequest != null, activeRequest };
}
